import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, inject, Input, OnChanges } from '@angular/core';
import { Params, Router } from '@angular/router';
import { restoreFocus } from '../a11y/focus';
import { ConfirmService } from '../confirm/confirm.service';

/** A named set of a list page's filters: its URL query, minus the page number. */
export interface SavedView {
  name: string;
  params: Params;
}

const STORAGE_PREFIX = 'admin-saved-views:';

/** The filters in a query, without paging, as a stable string for comparing. */
export function viewKey(params: Params): string {
  const { page: _page, ...rest } = params;
  return JSON.stringify(
    Object.keys(rest)
      .sort()
      .map((k) => [k, rest[k]]),
  );
}

/**
 * Saved views on a list page's filter bar (Airbnb's ops dashboards have them):
 * name the current filters, then open them again in one click. Kept in this
 * browser only. Opening a view loads its URL fresh, because most list pages
 * read their filters once when they open.
 */
@Component({
  selector: 'admin-saved-views',
  standalone: true,
  template: `
    @if (views.length || active) {
      <div class="admin-saved-views">
        <span class="admin-saved-views__label">Saved views</span>
        @for (v of views; track v.name; let i = $index) {
          <span class="admin-saved-views__view" [class.current]="isCurrent(v)">
            <button
              type="button"
              class="admin-saved-views__open"
              [attr.aria-current]="isCurrent(v) ? 'true' : null"
              (click)="open(v)"
            >
              {{ v.name }}
            </button>
            <button
              type="button"
              class="admin-saved-views__remove"
              [attr.aria-label]="'Delete saved view: ' + v.name"
              (click)="remove(v, i, $event)"
            >
              <span class="material-symbols-outlined" aria-hidden="true">close</span>
            </button>
          </span>
        }
        @if (active && !savedAlready()) {
          <button type="button" class="admin-saved-views__save" (click)="save($event)">
            <span class="material-symbols-outlined" aria-hidden="true">bookmark_add</span> Save these filters
          </button>
        }
      </div>
    }
  `,
  styles: [
    `
      .admin-saved-views {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid var(--cnt-line);
      }
      .admin-saved-views__label {
        margin-right: 4px;
        color: var(--cnt-muted);
        font-size: 0.75rem;
        font-weight: 700;
      }
      .admin-saved-views__view {
        display: inline-flex;
        align-items: center;
        border: 1px solid var(--cnt-line-strong);
        border-radius: 9999px;
        background: #fff;
      }
      .admin-saved-views__view.current {
        border-color: var(--cnt-green);
        background: rgba(41, 93, 66, 0.08);
      }
      .admin-saved-views__open,
      .admin-saved-views__remove,
      .admin-saved-views__save {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        border: 0;
        background: none;
        color: var(--cnt-ink);
        font: inherit;
        font-size: 0.8125rem;
        cursor: pointer;
      }
      .admin-saved-views__open {
        padding: 4px 4px 4px 12px;
        font-weight: 600;
      }
      .admin-saved-views__remove {
        padding: 4px 8px 4px 2px;
        color: var(--cnt-muted);
      }
      .admin-saved-views__remove .material-symbols-outlined,
      .admin-saved-views__save .material-symbols-outlined {
        font-size: 16px;
      }
      .admin-saved-views__save {
        padding: 4px 8px;
        color: var(--cnt-green);
        font-weight: 700;
      }
    `,
  ],
})
export class AdminSavedViewsComponent implements OnChanges {
  private readonly router = inject(Router);
  private readonly confirm = inject(ConfirmService);
  private readonly document = inject(DOCUMENT);
  // The filter bar is OnPush, and a save lands after the prompt closes, outside its events.
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Where this page's views are kept, e.g. "reservations". */
  @Input({ required: true }) key = '';
  /** Whether any filter is on, so there's something to save. */
  @Input() active = false;

  views: SavedView[] = [];

  ngOnChanges(): void {
    this.views = this.read();
  }

  private get current(): Params {
    return this.router.parseUrl(this.router.url).queryParams;
  }

  isCurrent(v: SavedView): boolean {
    return viewKey(v.params) === viewKey(this.current);
  }

  savedAlready(): boolean {
    return this.views.some((v) => this.isCurrent(v));
  }

  async save(event?: Event): Promise<void> {
    const trigger = (event?.currentTarget as Element | null) ?? null;
    const values = await this.confirm.prompt(
      'Save these filters',
      'Name this view, e.g. "Requests waiting".',
      [{ name: 'name', type: 'text', placeholder: 'Name' }],
      'Save view',
    );
    const name = values?.['name']?.trim();
    if (!name) return;
    if (
      this.views.some((v) => v.name === name) &&
      !(await this.confirm.confirm(`Replace "${name}"?`, 'A saved view already has this name.', 'Replace'))
    )
      return;
    const { page: _page, ...params } = this.current;
    this.views = [...this.views.filter((v) => v.name !== name), { name, params }];
    this.write();
    // "Save these filters" goes once they're saved: focus the new view instead.
    const index = this.views.length - 1;
    void restoreFocus(this.document, trigger, () => [this.buttons('open')[index]]);
  }

  /** Deletes a view; focus moves to the next view's delete button, the one before, or Save. */
  remove(v: SavedView, index = 0, event?: Event): void {
    const trigger = (event?.currentTarget as Element | null) ?? null;
    this.views = this.views.filter((x) => x !== v);
    this.write();
    void restoreFocus(this.document, trigger, () => {
      const removes = this.buttons('remove');
      return [
        removes[index],
        removes[index - 1],
        this.host.nativeElement.querySelector('.admin-saved-views__save'),
        this.host.nativeElement.closest('.admin-filter-bar')?.querySelector('ion-searchbar, ion-select, input, button'),
      ];
    });
  }

  private buttons(kind: 'open' | 'remove'): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(`.admin-saved-views__${kind}`));
  }

  open(v: SavedView): void {
    const path = this.router.url.split(/[?#]/)[0];
    this.load(this.router.serializeUrl(this.router.createUrlTree([path], { queryParams: v.params })));
  }

  /** A full load, since most list pages read their filters only when they open. */
  load(url: string): void {
    this.document.location.assign(url);
  }

  private read(): SavedView[] {
    try {
      const raw = JSON.parse(localStorage.getItem(STORAGE_PREFIX + this.key) ?? '[]');
      return Array.isArray(raw) ? raw.filter((v) => v && typeof v.name === 'string' && v.params) : [];
    } catch {
      return [];
    }
  }

  private write(): void {
    this.cdr.markForCheck();
    try {
      localStorage.setItem(STORAGE_PREFIX + this.key, JSON.stringify(this.views));
    } catch {
      // Storage blocked: the views last until the page reloads.
    }
  }
}
